/**
 * Merge editDrops for bulk edit initial values and mixed-option detection.
 * For Media Devoted | Created By Ciro Autiero
 */

import { toClickIdStructure, toDomainScheme } from '@trafficpulse/shared';

import type {
  BulkEditInitialValues,
  MixedOptions,
} from '@/features/smswiser-campaigns/components/prepared-drop-modals/bulk-edit.types';
import { isShortlinkMixed } from '@/features/smswiser-campaigns/utils/shortlinkOptionState';

export type { MixedOptions };

/** Minimal shape needed for bulk merge (from PreparedDrop or ScaleDropModal editDrops). */
export interface EditableDropFields {
  routeId: number;
  wiserId: number;
  shortLinkTldValue: string;
  amount: number;
  leadsListWiserId: number;
  executeAfter?: string | null;
  senderNames?: string[];
  smsTexts?: string[];
  batchAmount?: number | null;
  batchDelay?: number | null;
  forceSend?: boolean;
  aiOptimizationEnabled?: boolean;
  aiOptimizationType?: string | null;
  aiOptimizationBatchAmount?: number | null;
  aiOptimizationMinimalValuePercent?: number | null;
  aiOptimizationSendUntil?: string | null;
  useOnlyNewDomains?: boolean;
  clickIdStructure?: string | null;
  domainScheme?: string | null;
  staticTagId?: string | null;
}

export interface MergeBulkEditResult {
  initialValues: BulkEditInitialValues | undefined;
  mixedOptions: MixedOptions | undefined;
}

export function mergeBulkEditDrops(
  editDrops: EditableDropFields[] | undefined,
  bulkCount: number,
): MergeBulkEditResult {
  if (bulkCount <= 1 || !editDrops?.length) {
    return { initialValues: undefined, mixedOptions: undefined };
  }
  const forceSendVals = editDrops.map((d) => d.forceSend ?? false);
  const forceSendMixed = new Set(forceSendVals).size > 1;
  const sendInBatchesVals = editDrops.map(
    (d) => !!(d.batchAmount != null && d.batchDelay != null),
  );
  const sendInBatchesMixed = new Set(sendInBatchesVals).size > 1;
  const useOnlyNewDomainsVals = editDrops.map((d) => d.useOnlyNewDomains ?? false);
  const useOnlyNewDomainsMixed = new Set(useOnlyNewDomainsVals).size > 1;
  const clickIdVals = editDrops.map((d) => toClickIdStructure(d.clickIdStructure));
  const clickIdMixed = isShortlinkMixed(clickIdVals);
  const schemeVals = editDrops.map((d) => toDomainScheme(d.domainScheme));
  const schemeMixed = isShortlinkMixed(schemeVals);
  const staticTagIdVals = editDrops.map((d) => d.staticTagId ?? null);
  const staticTagIdMixed = new Set(staticTagIdVals.map((v) => v ?? '')).size > 1;
  const first = editDrops[0];
  const initialValues: BulkEditInitialValues = {
    routeId: first.routeId,
    wiserId: first.wiserId,
    shortLinkTldValue: first.shortLinkTldValue,
    amount: first.amount,
    leadsListWiserId: first.leadsListWiserId,
    executeAfter: first.executeAfter ?? null,
    senderNames: first.senderNames ?? [],
    smsTexts: first.smsTexts ?? [],
    batchAmount: sendInBatchesMixed ? null : (first.batchAmount ?? null),
    batchDelay: sendInBatchesMixed ? null : (first.batchDelay ?? null),
    forceSend: forceSendMixed ? undefined : (first.forceSend ?? false),
    aiOptimizationEnabled: first.aiOptimizationEnabled ?? false,
    aiOptimizationType:
      first.aiOptimizationType === 'ROI' || first.aiOptimizationType === 'CTR'
        ? first.aiOptimizationType
        : null,
    aiOptimizationBatchAmount: first.aiOptimizationBatchAmount ?? null,
    aiOptimizationMinimalValuePercent: first.aiOptimizationMinimalValuePercent ?? null,
    aiOptimizationSendUntil: first.aiOptimizationSendUntil ?? null,
    useOnlyNewDomains: useOnlyNewDomainsMixed ? undefined : (first.useOnlyNewDomains ?? false),
    clickIdStructure: clickIdMixed ? undefined : clickIdVals[0],
    domainScheme: schemeMixed ? undefined : schemeVals[0],
    staticTagId: staticTagIdMixed ? undefined : (first.staticTagId ?? null),
  };
  const mixedOptions: MixedOptions = {
    forceSend: forceSendMixed,
    sendInBatches: sendInBatchesMixed,
    useOnlyNewDomains: useOnlyNewDomainsMixed,
    clickIdStructure: clickIdMixed,
    domainScheme: schemeMixed,
    staticTagId: staticTagIdMixed,
  };
  return { initialValues, mixedOptions };
}
